import Tippy, {TippyProps} from '@tippyjs/react';
import classNames from 'classnames';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import React, {MouseEvent, MutableRefObject, ReactElement, ReactNode, useRef} from 'react';
import {Instance, Placement, Props} from 'tippy.js';
import './CLDropDownMenu.scss';
import CLDropDownMenuItem from './CLDropDownMenuItem';
import {AutomationProps} from 'common/src/modules/automation/automation.types';
import addAutomationTag from 'common/src/modules/automation/utils/addAutomationTag';

export const CL_DROPDOWN_ANIMATION_DURATION = 300;

export interface CLDropDownMenuItemProps {
  id: string;
  content: React.ReactNode;
  callBack?: () => void;
  subMenu?: CLDropDownMenuItemProps[];
  hideSubMenuArrow?: boolean;
  isDisabled?: boolean;
  isLineBreak?: boolean;
  isActive?: boolean;
  groupName?: string;
  tooltip?: ReactNode;
  hide?: boolean;
  onHover?: () => void;
  duration?: number;
}

export interface CLDropDownMenuProps extends Omit<TippyProps, 'children'>, AutomationProps {
  menuItems: CLDropDownMenuItemProps[];
  className?: string;
  dropdownClassName?: string;
  menuItemClassName?: string;
  triggerElementClassName?: string;
  width?: number;
  children?: ReactNode;
  triggerElement?: ReactElement;
  placement?: Placement;
  isMenuVisible?: boolean;
  animation?: string | boolean;
  offset?: [number, number];
  onClickOutside?: (instance?: Instance<Props>, event?: Event) => void;
  onMouseLeave?: () => void;
  onScroll?: () => void;
  callbackOnHover?: boolean;
  duration?: number;
  disableOnShow?: boolean;
  maxWidth?: number;
  highlightOnlyActiveItems?: boolean;
}

function CLDropDownMenu({
  menuItems,
  className = '',
  dropdownClassName = '',
  menuItemClassName = '',
  triggerElementClassName = '',
  width,
  triggerElement,
  placement = 'bottom',
  isMenuVisible,
  duration = CL_DROPDOWN_ANIMATION_DURATION,
  animation = 'fade',
  offset = [0, 2],
  onClickOutside = () => {},
  onMouseLeave = () => {},
  onScroll = () => {},
  callbackOnHover = false,
  disableOnShow,
  children,
  maxWidth,
  highlightOnlyActiveItems,
  qaHook = '',
  ...tippyProps
}: CLDropDownMenuProps) {
  const listStyle = (width && {width: `${width}px`}) || {};
  const activeItemRef: MutableRefObject<HTMLLIElement | null> = useRef(null);

  const visibleMenuItems = _reject(menuItems, 'hide');

  function onMenuItemPicked(e: MouseEvent, item: CLDropDownMenuItemProps, isEnabled: boolean) {
    if (!isEnabled) return;

    e.stopPropagation();

    if (!item.isDisabled && item.callBack) {
      item.callBack();
    }
  }

  return (
    <div className={classNames('common_cl_dropdown_menu', className)} {...addAutomationTag(qaHook)}>
      <Tippy
        {...tippyProps}
        content={
          <ul
            className={dropdownClassName}
            style={listStyle}
            onMouseLeave={onMouseLeave}
            onScroll={onScroll}
          >
            {_map(visibleMenuItems, (item) => {
              return (
                <CLDropDownMenuItem
                  qaHook={qaHook && `${qaHook}_item_${item.id}`}
                  key={item.id}
                  onMouseOver={(e) => {
                    item.onHover?.();
                    onMenuItemPicked(e, item, callbackOnHover);
                  }}
                  onClick={(e) => onMenuItemPicked(e, item, !callbackOnHover)}
                  item={item}
                  menuItemClassName={menuItemClassName}
                  itemRef={(el) => {
                    item.isActive && (activeItemRef.current = el);
                  }}
                  highlightOnlyActiveItems={highlightOnlyActiveItems}
                />
              );
            })}
          </ul>
        }
        onClickOutside={onClickOutside}
        animation={animation}
        arrow={true}
        interactive={true}
        offset={offset}
        placement={placement}
        duration={duration}
        appendTo="parent"
        maxWidth={maxWidth}
        visible={isMenuVisible}
        onShow={(x) => {
          !disableOnShow && setTimeout(() => activeItemRef.current?.scrollIntoView(true), 0);
          tippyProps.onShow?.(x);
        }}
      >
        <span className={classNames(triggerElementClassName)}>{triggerElement || children}</span>
      </Tippy>
    </div>
  );
}

export default CLDropDownMenu;
